'use client';
import { get, isEmpty, size, toNumber } from 'lodash';
import { useEffect, useState } from 'react';

import { Select, Tooltip, Icon } from '@repo/ui';
import { APPEAL_STATUS_LABEL } from '@repo/types';

import { QueryStringParams } from '@xtnd/web/shared/types';
import type { SelectOption } from '@repo/ui';
import { AppealStatus, AppealType } from '@repo/types';
import { useDarkMode } from '@xtnd/web/shared/hooks/useDarkMode';

import {
  Appeal,
  FileCategory,
  MatterInvoiceSummaryFile,
  UpdateAppealStatusPayload,
} from '../../types';
import { useUpdateAppealStatus, useUploadMatterInvoiceSummaryFiles } from '../../queries';
import {
  APPEAL_STATUS_OPTIONS,
  APPEAL_STATUS_TEXT_COLOR,
} from '../../constants';

import MarkNoAppealModal from './MarkNoAppeal';
import { MarkToBeAppealedModal } from './MarkToBeAppealed';
import AppealResolutionModal from './MarkAppealDenied';
import MarkPartialWriteOffModal from '../common/MarkPartialWriteOff';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { MarkAppealSubmittedModal } from './MarkAppealSubmitted';
import { MarkAppealApprovedModal } from './MarkAppealApproved';

interface AppealStatusBadgeProps {
  params: QueryStringParams;
  currentStatus: AppealStatus;
  invoiceId: string;
  invoiceNumber?: string;
  appealId?: string;
  creditsIssued?: number;
  basicInformation?: Partial<Appeal>;
  onMutationStateChange?: (invoiceId: string, isPending: boolean) => void;
  isAppealSelected?: boolean;
}

type ModalType =
  | 'appealSubmitted'
  | 'noAppeal'
  | 'toBeAppealed'
  | 'toBeAppealedBacklog'
  | 'appealDenied'
  | 'discountOnly'
  | 'appealApproved'
  | 'deadlineExpired'
  | null;

function LoadingDots({
  isLoading = false,
  size = 'h-1 w-1',
  className = 'text-xtnd-blue',
  gap = 'gap-1',
  ariaLabel = 'Loading',
}) {
  const delays = ['0s', '0.15s', '0.3s'];

  if (!isLoading) return null;

  return (
    <output aria-label={ariaLabel} className={`inline-flex items-center ${gap} ${className}`}>
      {delays.map((delay, index) => (
        <span
          key={`loading-dot-${index}`}
          className={`inline-block rounded-full ${size} bg-current !animate-dot-pulse`}
          style={{ animationDelay: delay }}
        />
      ))}
    </output>
  );
}

export default function AppealStatusBadge({
  invoiceId,
  invoiceNumber,
  params,
  currentStatus,
  basicInformation,
  onMutationStateChange,
  isAppealSelected = false,
}: AppealStatusBadgeProps) {
  const { mutateAsync: updateAppealStatus, isPending } = useUpdateAppealStatus(
    params,
    isAppealSelected
  );

  const { mutateAsync: uploadFiles } = useUploadMatterInvoiceSummaryFiles(invoiceId, 'AR Appeal', {
    isShowToast: false,
    category: FileCategory.PAYMENT_SUPPORT,
  });
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [selectedStatus, setSelectedStatus] = useState<AppealStatus>(currentStatus);
  const [isLocalPending, setIsLocalPending] = useState(false);

  const [isLoading, setIsLoading] = useState<{
    toBeAppealed: boolean;
    fileUpload: boolean;
    discountOnly: boolean;
  }>({
    toBeAppealed: false,
    fileUpload: false,
    discountOnly: false,
  });

  const { isDark } = useDarkMode();
  const mode = isDark ? 'dark' : 'light';

  // Sync selectedStatus with currentStatus when it changes from cache updates/refetches,
  // but only when no mutation or modal is active to avoid premature resets
  useEffect(() => {
    if (!isPending && !isLocalPending && activeModal === null) {
      setSelectedStatus(currentStatus);
    }
  }, [currentStatus, isPending, isLocalPending, activeModal]);

  const APPEAL_STATUS_FLOW = {
    PRE_APPEAL: [
      APPEAL_STATUS_OPTIONS[mode].PRE_APPEAL,
      APPEAL_STATUS_OPTIONS[mode].TO_BE_APPEALED,
      APPEAL_STATUS_OPTIONS[mode].APPEAL_SUBMITTED_BACKLOG,
      APPEAL_STATUS_OPTIONS[mode].NO_APPEAL,
      APPEAL_STATUS_OPTIONS[mode].DEADLINE_EXPIRED,
    ],
    TO_BE_APPEALED: [
      APPEAL_STATUS_OPTIONS[mode].TO_BE_APPEALED,
      APPEAL_STATUS_OPTIONS[mode].NO_APPEAL,
      APPEAL_STATUS_OPTIONS[mode].APPEAL_SUBMITTED,
    ],
    AWAITING_APPEAL_SUBMISSION: [
      APPEAL_STATUS_OPTIONS[mode].AWAITING_APPEAL_SUBMISSION,
      APPEAL_STATUS_OPTIONS[mode].NO_APPEAL,
      APPEAL_STATUS_OPTIONS[mode].APPEAL_SUBMITTED,
    ],
    APPEAL_SUBMITTED: [
      APPEAL_STATUS_OPTIONS[mode].APPEAL_SUBMITTED,
      APPEAL_STATUS_OPTIONS[mode].APPEAL_APPROVED,
      APPEAL_STATUS_OPTIONS[mode].APPEAL_DENIED,
      APPEAL_STATUS_OPTIONS[mode].DEADLINE_EXPIRED,
    ],
  };

  const renderSelectStatusOptions = (currentStatus: AppealStatus) => {
    return get(APPEAL_STATUS_FLOW, currentStatus, []);
  };

  const renderFinalStatus = (currentStatus: AppealStatus) => {
    const label = get(APPEAL_STATUS_LABEL, currentStatus, currentStatus);
    const mode = isDark ? 'dark' : 'light';
    const color = get(
      APPEAL_STATUS_TEXT_COLOR,
      `${currentStatus}.${mode}`,
      APPEAL_STATUS_TEXT_COLOR.PRE_APPEAL[mode]
    );

    return (
      <Tooltip
        content={'This status cannot be changed once resolved.'}
        position="auto"
        className="w-[12rem] max-w-[12rem] rounded-md"
        contentClassName="min-w-[16.25rem]"
      >
        <div
          className={`min-h-[2.375rem] bg-xtnd-white flex items-center opacity-80 justify-between  border border-xtnd-light
  dark:border-xtnd-dark-600 dark:bg-xtnd-dark-800 rounded-md cursor-not-allowed truncate`}
          title={label}
        >
          <div className="flex items-center gap-2 justify-between px-2 w-full">
            <p className={`text-sm truncate w-[8rem]`} style={{ color }}>
              {label}
            </p>
            <LoadingDots isLoading={isPending || isLocalPending} />
          </div>

          <div className="flex h-full min-w-[41px] justify-between items-center">
            <span className="self-stretch w-px bg-xtnd-light dark:bg-xtnd-dark-400 my-2"></span>
            <div className="h-[2.25rem] w-[2.25rem] flex items-center justify-center">
              <Icon
                name="HiLockClosed"
                size="sm"
                className="text-xtnd-dark-600 dark:text-xtnd-dark-300"
              />
            </div>
          </div>
        </div>
      </Tooltip>
    );
  };

  const handleCallStatusUpdate = async (payload: UpdateAppealStatusPayload) => {
    setIsLocalPending(true);
    onMutationStateChange?.(invoiceId, true);
    try {
      await updateAppealStatus(payload);
    } finally {
      onMutationStateChange?.(invoiceId, false);
      setIsLocalPending(false);
      setActiveModal(null);
    }
  };

  const handleStatusModalSubmit = async (payload: Partial<UpdateAppealStatusPayload>) => {
    setSelectedStatus(payload.nextStatus as AppealStatus);
    try {
      await handleCallStatusUpdate({
        invoiceId,
        nextStatus: payload.nextStatus as AppealStatus,
        ...payload,
      });
    } catch (err) {
      setSelectedStatus(currentStatus);
    }
  };

  const handleSubmitAppealFileUpload = async (files: File[], comment: string) => {
    setIsLoading(prev => ({ ...prev, fileUpload: true }));
    try {
      // Only upload files if there are files and the status is NO_APPEAL
      if (size(files) > 0 && selectedStatus === AppealStatus.NO_APPEAL) {
        const uploadResult = await uploadFiles({
          files: files,
        });

        // Check if there are any error files and stop the process
        if (uploadResult.errorFiles && uploadResult.errorFiles.length > 0) {
          return;
        }
      }

      await updateAppealStatus({
        invoiceId,
        nextStatus: AppealStatus.NO_APPEAL,
        comment,
      });
      setSelectedStatus(AppealStatus.NO_APPEAL);
      setActiveModal(null);
    } finally {
      setIsLoading(prev => ({ ...prev, fileUpload: false }));
    }
  };

  const handleSubmitAppealDenied = async (payload: {
    comment: string;
    files: File[];
    selectedExistingFiles: MatterInvoiceSummaryFile[];
    targetStatus: AppealStatus;
  }) => {
    setIsLoading(prev => ({ ...prev, fileUpload: true }));
    try {
      let filesPayload = [] as MatterInvoiceSummaryFile[];

      if (size(payload.selectedExistingFiles) > 0) {
        filesPayload = [...filesPayload, ...payload.selectedExistingFiles];
      }

      if (size(payload.files) > 0) {
        const uploadResult = await uploadFiles({
          files: payload.files,
          successCallback: async response => {
            filesPayload = [
              ...filesPayload,
              ...(get(response, 'successFiles', []) as MatterInvoiceSummaryFile[]),
            ];
          },
        });

        if (uploadResult.errorFiles && uploadResult.errorFiles.length > 0) {
          return;
        }
      }

      await updateAppealStatus({
        invoiceId,
        nextStatus: payload.targetStatus,
        comment: payload.comment,
        files: filesPayload,
      });

      setSelectedStatus(payload.targetStatus);
      setActiveModal(null);
    } finally {
      setIsLoading(prev => ({ ...prev, fileUpload: false }));
    }
  };

  const handleSubmitMarkToBeAppealed = async (payload: UpdateAppealStatusPayload) => {
    setIsLoading(prev => ({ ...prev, toBeAppealed: true }));
    let appealedFees;
    let appealedExpenses;

    try {
      if (payload.appealType === AppealType.BACKLOG) {
        setSelectedStatus(AppealStatus.APPEAL_SUBMITTED);
        appealedFees = toNumber(payload.appealedFees);
        appealedExpenses = toNumber(payload.appealedExpenses);
      }

      await updateAppealStatus({
        invoiceId,
        nextStatus: AppealStatus.TO_BE_APPEALED,
        comment: payload.comment,
        remainingAmount: toNumber(payload.remainingAmount),
        appealType: payload.appealType,
        deadline: payload.deadline,
        attyDeadline: payload.attyDeadline,
        feesCut: toNumber(payload.feesCut),
        expensesCut: toNumber(payload.expensesCut),
        promptPayment: toNumber(payload.promptPayment),
        bulkDiscount: toNumber(payload.bulkDiscount),
        administrativeFee: toNumber(payload.administrativeFee),
        appealedFees,
        appealedExpenses,
        timekeepers: payload.timekeepers,
        files: payload.files,
      });

      setActiveModal(null);
    } finally {
      setIsLoading(prev => ({ ...prev, toBeAppealed: false }));
    }
  };

  const STATUS_TO_MODAL: Partial<Record<AppealStatus, ModalType>> = {
    [AppealStatus.TO_BE_APPEALED]: 'toBeAppealed',
    [AppealStatus.NO_APPEAL]: 'discountOnly',
    [AppealStatus.APPEAL_DENIED]: 'appealDenied',
    [AppealStatus.DEADLINE_EXPIRED]: 'discountOnly',
    [AppealStatus.APPEAL_SUBMITTED]: 'appealSubmitted',
    [AppealStatus.APPEAL_APPROVED]: 'appealApproved',
  };

  const closeModal = () => {
    if (isPending || isLocalPending) return;
    setActiveModal(null);
    setSelectedStatus(currentStatus);
  };

  const handleChangeStatus = async (invoiceId: string, selectedStatus: AppealStatus) => {
    if (currentStatus === selectedStatus || !selectedStatus) {
      return;
    }

    // When selecting Appeal Submitted from PRE_APPEAL, open the backlog form directly
    if (
      currentStatus === AppealStatus.PRE_APPEAL &&
      selectedStatus === AppealStatus.APPEAL_SUBMITTED
    ) {
      setSelectedStatus(selectedStatus);
      setActiveModal('toBeAppealedBacklog');
      return;
    }

    setSelectedStatus(selectedStatus);

    const modalType = STATUS_TO_MODAL[selectedStatus];
    if (modalType) {
      setActiveModal(modalType);
      return;
    }

    await handleCallStatusUpdate({
      invoiceId,
      nextStatus: selectedStatus,
    });
  };

  const handleSubmitMarkDiscountOnly = async (payload: UpdateAppealStatusPayload) => {
    setIsLoading(prev => ({ ...prev, discountOnly: true }));
    try {
      setSelectedStatus(payload.nextStatus ?? AppealStatus.NO_APPEAL);
      await updateAppealStatus(payload);
      setActiveModal(null);
    } finally {
      setIsLoading(prev => ({ ...prev, discountOnly: false }));
    }
  };

  const options = renderSelectStatusOptions(currentStatus);
  if (isEmpty(options)) {
    return renderFinalStatus(currentStatus);
  }

  return (
    <div className="w-[12rem]">
      <Select
        options={options as SelectOption[]}
        disabled={isPending || isLocalPending}
        isLoading={isPending || isLocalPending}
        value={selectedStatus}
        onChange={selectedStatus =>
          handleChangeStatus(invoiceId, (selectedStatus ?? '') as AppealStatus)
        }
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        isDarkMode={isDark}
        className="text-sm"
        searchable={false}
        classNamePrefix="react-select"
        menuPosition="fixed"
        menuPlacement="auto"
        fullWidth
      />

      <MarkAppealSubmittedModal
        invoiceId={invoiceId}
        invoiceNumber={invoiceNumber ?? ''}
        selectedStatus={selectedStatus}
        isOpen={activeModal === 'appealSubmitted'}
        isLoading={isLoading.fileUpload}
        basicInformation={basicInformation}
        onClose={closeModal}
        onSubmit={handleStatusModalSubmit}
      />

      <MarkAppealApprovedModal
        invoiceId={invoiceId}
        invoiceNumber={invoiceNumber ?? ''}
        isOpen={activeModal === 'appealApproved'}
        selectedStatus={selectedStatus}
        isLoading={isLoading.fileUpload}
        basicInformation={basicInformation}
        onClose={closeModal}
        onSubmit={handleStatusModalSubmit}
      />

      <MarkNoAppealModal
        invoiceId={invoiceId}
        isOpen={activeModal === 'noAppeal'}
        isLoading={isLoading.fileUpload}
        basicInformation={basicInformation}
        onClose={closeModal}
        onSubmit={handleSubmitAppealFileUpload}
      />

      <AppealResolutionModal
        invoiceId={invoiceId}
        isOpen={activeModal === 'appealDenied'}
        isLoading={isLoading.fileUpload}
        basicInformation={basicInformation}
        title={`Appeal Denied - INV #${invoiceNumber ?? ''}`}
        warningText="When marking an appeal as denied, the outstanding balance may be written off. Attach supporting documentation for this decision."
        isCommentRequired={true}
        isFilesRequired={true}
        targetStatus={AppealStatus.APPEAL_DENIED}
        onClose={closeModal}
        onSubmit={handleSubmitAppealDenied}
      />

      <MarkToBeAppealedModal
        isOpen={activeModal === 'toBeAppealed'}
        invoiceNumber={invoiceNumber ?? ''}
        invoiceId={invoiceId}
        isLoading={isLoading.toBeAppealed}
        onSubmit={handleSubmitMarkToBeAppealed}
        setLoading={loading => setIsLoading(prev => ({ ...prev, ...loading }))}
        onClose={(success = false, appealType = AppealType.SUBSEQUENT) => {
          if (isPending || isLocalPending) return;
          setActiveModal(null);
          if (success) {
            setSelectedStatus(
              appealType === AppealType.BACKLOG
                ? AppealStatus.APPEAL_SUBMITTED
                : AppealStatus.TO_BE_APPEALED
            );
            return;
          }
          setSelectedStatus(currentStatus);
        }}
      />

      <MarkToBeAppealedModal
        isOpen={activeModal === 'toBeAppealedBacklog'}
        invoiceNumber={invoiceNumber ?? ''}
        invoiceId={invoiceId}
        isLoading={isLoading.toBeAppealed}
        initialAppealType={AppealType.BACKLOG}
        onSubmit={handleSubmitMarkToBeAppealed}
        setLoading={loading => setIsLoading(prev => ({ ...prev, ...loading }))}
        onClose={(success = false) => {
          if (isPending || isLocalPending) return;
          setActiveModal(null);
          if (success) {
            setSelectedStatus(AppealStatus.APPEAL_SUBMITTED);
            return;
          }
          setSelectedStatus(currentStatus);
        }}
      />

      <MarkPartialWriteOffModal
        isOpen={activeModal === 'discountOnly'}
        invoiceId={invoiceId}
        invoiceNumber={invoiceNumber ?? ''}
        isLoading={isLoading.discountOnly}
        disabled={isLoading.discountOnly}
        targetStatus={selectedStatus}
        onSubmit={handleSubmitMarkDiscountOnly}
        setLoading={loading => setIsLoading(prev => ({ ...prev, ...loading }))}
        onClose={(success = false) => {
          if (isPending || isLocalPending) return;
          setActiveModal(null);
          if (success) {
            setSelectedStatus(selectedStatus);
            return;
          }
          setSelectedStatus(currentStatus);
        }}
      />

    </div>
  );
}
